
import React from 'react';
import {connect} from 'react-redux';
import classNames from 'classnames';
/* import FinishSound from '../../components/FinishSound'; */

import {updateObject} from '../../utils/reducerUtils';

import AudioPlayer from '../../components/AudioPlayer';

import strings from '../../utils/strings';
import {VARIATION} from '../../constants';

const maxAudioPlayCount = 2;

class Test extends React.Component {
    constructor() {
        super();

        const variation = VARIATION;
        const language = strings.getLanguage();

        const audioFiles = require(
            `../../assets/testdata/${variation}/${language}/dictation-audioFiles`
        ).default;

        this.taskIsOver =
            language === 'de'
                ? require(
                      `../../assets/sounds/${variation}/${language}/taskIsOver.mp3`
                  )
                : null;

        this.audioFiles = audioFiles;
        this.audioPlayerRefs = {};
        this.inputRefs = {};
        this.assignmentRefs = {};
        this.preloadedAudios = [];

        this.assignments = Object.keys(this.audioFiles)
            .map(Number)
            .sort((a, b) => a - b)
            .map((id) => ({
                id,
                value: '',
            }));

        const results = {};

        for (const assignment of this.assignments) {
            results[assignment.id] = assignment.value;
        }

        this.state = {
            results,
            disabledAssignments: {},
            audioPlayCounts: {},
            renderFinalSound: null,
            activeAssignmentId: null,
            showMissing: false,
        };

        this.handleNextStep = this.handleNextStep.bind(this);
    }

    componentDidMount() {
        // Warm up the first few audio files so the first playback
        // does not stutter.
        [1, 2, 3].forEach((id) => {
            const src = this.audioFiles[id];

            if (src) {
                const audio = new Audio();
                audio.preload = 'auto';
                audio.src = src;
                audio.load();

                this.preloadedAudios.push(audio);
            }
        });
    }

    componentWillUnmount() {
        this.preloadedAudios.forEach((audio) => {
            audio.pause();
            audio.src = '';
        });

        this.preloadedAudios = [];
    }

    setResults(id, value) {
        this.setState((prevState) => ({
            results: updateObject(prevState.results, {
                [id]: value,
            }),
        }));
    }

    handleAssignmentFocus(assignmentId, assignmentAudioPlayCount) {
        this.setState(
            (prevState) => {
                const nextDisabledAssignments = {
                    ...prevState.disabledAssignments,
                };

                const previousAssignmentId =
                    prevState.activeAssignmentId;

                if (
                    previousAssignmentId &&
                    previousAssignmentId !== assignmentId &&
                    (prevState.results[previousAssignmentId] || '').trim()
                ) {
                    nextDisabledAssignments[previousAssignmentId] = true;
                }

                return {
                    disabledAssignments: nextDisabledAssignments,
                    activeAssignmentId: assignmentId,
                };
            },
            () => {
                if (assignmentAudioPlayCount === 0) {
                    window.requestAnimationFrame(() => {
                        const playerRef =
                            this.audioPlayerRefs[assignmentId];

                        if (playerRef && playerRef.handleStartPlayer) {
                            playerRef.handleStartPlayer();
                        }
                    });
                }
            }
        );
    }

    focusNextAssignment(assignmentId) {
        const currentIndex = this.assignments.findIndex(
            (assignment) => assignment.id === assignmentId
        );

        if (currentIndex === -1) {
            return;
        }

        const nextAssignment = this.assignments[currentIndex + 1];

        if (!nextAssignment) {
            return;
        }

        const nextInput = this.inputRefs[nextAssignment.id];

        if (nextInput && !nextInput.disabled) {
            nextInput.focus();
        }
    }

    handleAssignmentKeyDown(ev, assignmentId) {
        const isEnter = ev.key === 'Enter';
        const isForwardTab = ev.key === 'Tab' && !ev.shiftKey;

        if (!isEnter && !isForwardTab) {
            return;
        }

        const currentIndex = this.assignments.findIndex(
            (assignment) => assignment.id === assignmentId
        );

        const isLastAssignment =
            currentIndex === this.assignments.length - 1;

        if (isLastAssignment) {
            if (isEnter) {
                ev.preventDefault();
                ev.currentTarget.blur();
            }

            return;
        }

        ev.preventDefault();
        this.focusNextAssignment(assignmentId);
    }

    // Validate answers before allowing the user to continue.
    handleNextStep() {
        const {results} = this.state;
        const {onNextStep} = this.props;

        const firstUnanswered = this.assignments.find((assignment) => {
            const value = results[assignment.id];

            return (
                typeof value !== 'string' ||
                value.trim().length === 0
            );
        });

        // Highlight missing answers and focus the first one.
        if (firstUnanswered) {
            this.setState({showMissing: true}, () => {
                const assignmentId = firstUnanswered.id;

                const element = this.assignmentRefs[assignmentId];
                const input = this.inputRefs[assignmentId];

                if (input && !input.disabled) {
                    input.focus({preventScroll: true});
                }

                if (element) {
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            });

            return;
        }

        // All assignments have answers.
        // Preserve the original submission format.
        onNextStep(results);
    }

    render() {
        const {refs} = this.props;

        const {
            audioPlayCounts,
            disabledAssignments,
            results,
            showMissing,
        } = this.state;

        const hasUnanswered = this.assignments.some((assignment) => {
            const value = results[assignment.id];

            return (
                typeof value !== 'string' ||
                value.trim().length === 0
            );
        });

        const showValidation = showMissing && hasUnanswered;

        const validationMessage =
            strings.getLanguage() === 'de'
                ? 'Bitte füllen Sie alle markierten Felder aus, bevor Sie fortfahren.'
                : strings.getLanguage() === 'nn'
                ? 'Fyll ut alle dei markerte felta før du går vidare.'
                : 'Fyll ut alle de markerte feltene før du går videre.';

        return (
            <div className="lit-wrapper--thin lit-test lit-diktat">
                {showValidation && (
                    <p
                        className="lit-validation-message"
                        id="diktat-validation-message"
                        role="alert"
                    >
                        {validationMessage}
                    </p>
                )}

                {this.assignments.map((assignment) => {
                    const assignmentId = assignment.id;
                    const audioFile = this.audioFiles[assignmentId];

                    const assignmentAudioPlayCount =
                        audioPlayCounts[assignmentId] || 0;

                    const lastRow =
                        assignmentId ===
                        this.assignments[this.assignments.length - 1].id;

                    const isDisabled =
                        disabledAssignments[assignmentId];

                    const isAudioDisabled =
                        isDisabled ||
                        assignmentAudioPlayCount >= maxAudioPlayCount;

                    const currentValue = results[assignmentId];

                    const isUnanswered =
                        showValidation &&
                        (
                            typeof currentValue !== 'string' ||
                            currentValue.trim().length === 0
                        );

                    return (
                        <div
                            className={classNames(
                                'lit-input lit-revealed',
                                {
                                    'lit-input--disabled': isDisabled,
                                    'lit-input--unanswered': isUnanswered,
                                }
                            )}
                            key={assignmentId}
                            ref={(ref) => {
                                this.assignmentRefs[assignmentId] = ref;
                            }}
                        >
                            <AudioPlayer
                                disabled={isAudioDisabled}
                                id={assignmentId}
                                onComplete={() => {
                                    this.setState((prevState) => ({
                                        audioPlayCounts: updateObject(
                                            prevState.audioPlayCounts,
                                            {
                                                [assignmentId]: Math.min(
                                                    (
                                                        prevState.audioPlayCounts[
                                                            assignmentId
                                                        ] || 0
                                                    ) + 1,
                                                    maxAudioPlayCount
                                                ),
                                            }
                                        ),
                                        ...(lastRow
                                            ? {renderFinalSound: true}
                                            : {}),
                                    }));
                                }}
                                onStart={() => {}}
                                ref={(ref) => {
                                    this.audioPlayerRefs[assignmentId] = ref;
                                }}
                                refs={refs}
                                src={audioFile}
                                preload="none"
                            />

                            <input
                                className="lit-input__field"
                                disabled={isDisabled}
                                onChange={(ev) => {
                                    if (isDisabled) {
                                        return;
                                    }

                                    this.setResults(
                                        assignmentId,
                                        ev.target.value
                                    );
                                }}
                                onFocus={() => {
                                    this.handleAssignmentFocus(
                                        assignmentId,
                                        assignmentAudioPlayCount
                                    );
                                }}
                                onKeyDown={(ev) => {
                                    this.handleAssignmentKeyDown(
                                        ev,
                                        assignmentId
                                    );
                                }}
                                ref={(ref) => {
                                    this.inputRefs[assignmentId] = ref;
                                }}
                                placeholder={strings.writeWordHere}
                                spellCheck="false"
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="none"
                                inputMode="text"
                                type="text"
                                aria-autocomplete="none"
                                aria-invalid={isUnanswered}
                                aria-describedby={
                                    isUnanswered
                                        ? 'diktat-validation-message'
                                        : undefined
                                }
                            />
                        </div>
                    );
                })}

                <div>
                    <span>
                        {/*
                        <FinishSound
                            renderFinalSound={this.state.renderFinalSound}
                            text={strings.Dictation_is_Finished}
                            audioSrc={this.taskIsOver}
                        />
                        */}

                        <button
                            className="lit-btn lit bg-primary"
                            onClick={this.handleNextStep}
                        >
                            {strings.done}
                        </button>
                    </span>
                </div>
            </div>
        );
    }
}

const mapStateToProps = () => {
    return {};
};

export default connect(mapStateToProps)(Test);
